import type { BaseHttpRequest } from '@/api/generated-client/core/BaseHttpRequest';
import { BaseApi } from '@/api/base-api';
import type {
  TagKind,
  TagSchemaType,
  TagAssignmentMode,
  TagState,
  TagValue,
} from './TagsApi';

export type GameTagState = 'enabled' | 'disabled';

export type GameTagsListItemResponse = {
  tagId: string;
  tagSlug: string;
  kind: TagKind;
  schemaType: TagSchemaType;
  assignmentMode: TagAssignmentMode;
  assigned: boolean;
  tagState: TagState;
  tagValue: TagValue;
  gameTagState: GameTagState;
  gameTagValue: TagValue | null;
  resolvedState: GameTagState;
};

export type GameTagsListResponse = {
  gameSlug: string;
  items: GameTagsListItemResponse[];
};

export type GameTagsUpdateItem = {
  tagId: string;
  state: GameTagState;
};

export type GameTagsUpdateRequest = {
  items: GameTagsUpdateItem[];
};

export type GameTagsUpdateFailedItemResponse = {
  tagId: string;
  reason: GameTagsUpdateFailureReason;
};

export type GameTagsUpdateFailureReason =
  | 'tag_not_found'
  | 'tag_assignment_not_allowed'
  | 'tag_incompatible'
  | 'too_many_ui_badges';

export type GameTagsUpdateResponse = {
  gameSlug: string;
  success: boolean;
  requestedCount: number;
  affectedCount: number;
  failedItems: GameTagsUpdateFailedItemResponse[];
};

export type TagGamesRequest = {
  gameSlugs: string[];
};

export type TagGamesFailedItemResponse = {
  gameSlug: string;
  reason: TagGamesFailureReason;
};

export type TagGamesFailureReason =
  | 'game_not_found'
  | 'tag_incompatible'
  | 'too_many_ui_badges';

export type TagGamesResponse = {
  success: boolean;
  tagId: string;
  requestedCount: number;
  affectedCount: number;
  failedItems: TagGamesFailedItemResponse[];
};

export class GameTagsApi extends BaseApi {
  private controller = '/common/catalog';

  constructor(public readonly httpRequest: BaseHttpRequest) {
    super();
  }

  public async getGameTags(
    gameSlug: string
  ): Promise<GameTagsListResponse> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/games/${gameSlug}/tags`,
      {}
    );
  }

  public async updateGameTags(
    gameSlug: string,
    payload: GameTagsUpdateRequest
  ): Promise<GameTagsUpdateResponse> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[PUT] /api/v1/games/${gameSlug}/tags`,
      payload
    );
  }

  public async addGamesToTag(
    tagId: string,
    payload: TagGamesRequest
  ): Promise<TagGamesResponse> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[POST] /api/v1/tags/${tagId}/games`,
      payload
    );
  }
}
